/* eslint-disable indent */
import React, { useContext, useEffect, useState } from "react";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Flame, Heart, Image as ImageIcon, User, UserMinus, UserPlus, Users } from "lucide-react";
import Link from "next/link";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { followUser, unfollowUser } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IUserDTO, UserPremiumType } from "types";
import { colourScheme } from "utils/styling/colours";
import { AvatarImage, AvatarWrapper, CardContainer, Content, DefaultAvatar, FollowButton, Header, StatIcon, StatItem, StatLabel, StatsContainer, StatText, StatValue, UserInfo, Username } from "./UserCard.styled";
interface UserCardProps {
  user: IUserDTO;
}
const UserCard: React.FC<UserCardProps> = ({
  user
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [isFollowing, setIsFollowing] = useState(false);
  const subscriptionStyles = {
    NONE: {
      borderColor: colourScheme.greyLight,
      animation: "none"
    },
    BASIC: {
      borderColor: colourScheme.secondaryDark,
      animation: "none"
    },
    PLUS: {
      borderColor: colourScheme.errorDark,
      animation: "subtlePulse"
    },
    PRO: {
      borderColor: colourScheme.alert,
      animation: "subtlePulse"
    },
    ELITE: {
      gradientBorder: `linear-gradient(45deg, ${colourScheme.success}, ${colourScheme.alert}, ${colourScheme.secondary})`,
      animation: "subtlePulse"
    },
    ULTIMATE: {
      gradientBorder: `linear-gradient(45deg, ${colourScheme.secondaryDark}, ${colourScheme.errorDark}, ${colourScheme.alert})`,
      animation: "subtlePulse 3s infinite, gradientShift 5s linear infinite"
    }
  };
  const subscriptionStyle = subscriptionStyles[user.subscriptionType ?? UserPremiumType.NONE] || subscriptionStyles[UserPremiumType.NONE];
  useEffect(() => {
    if (state.user && user) {
      const isUserFollowing = state.user.following?.some(followingUser => followingUser.userId === user.userId);
      setIsFollowing(!!isUserFollowing);
    }
  }, [state.user, user]);
  const handleFollowToggle = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation when clicking the button

    if (!state.isLoggedIn || !state.user) {
      // Prompt user to log in
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    try {
      if (isFollowing) {
        // Unfollow the user
        const response = await unfollowUser(user.userId);
        if (response.success) {
          setIsFollowing(false);
          dispatch({
            type: AppReducerActionType.SET_USER,
            payload: {
              ...state,
              user: {
                ...state.user,
                following: state.user.following?.filter(followingUser => followingUser.userId !== user.userId)
              }
            }
          });
          // Update global state if necessary
        } else {
          console.error("Error unfollowing user:", response.error);
        }
      } else {
        // Follow the user
        const response = await followUser(user.userId);
        if (response.success) {
          setIsFollowing(true);
          dispatch({
            type: AppReducerActionType.SET_USER,
            payload: {
              ...state,
              user: {
                ...state.user,
                following: [...(state.user.following || []), {
                  userId: user.userId,
                  username: user.username,
                  avatarUrl: user.avatarUrl,
                  streak: user.streak
                }]
              }
            }
          });
          // Update global state if necessary
        } else {
          console.error("Error following user:", response.error);
        }
      }
    } catch (error) {
      console.error("Error toggling follow state:", error);
    }
  };
  return <Link href={`/profile/${user.username}`} passHref data-sentry-element="Link" data-sentry-component="UserCard" data-sentry-source-file="index.tsx">
            <CardContainer data-sentry-element="CardContainer" data-sentry-source-file="index.tsx">
                <Content data-sentry-element="Content" data-sentry-source-file="index.tsx">
                    <Header data-sentry-element="Header" data-sentry-source-file="index.tsx">
                        <AvatarWrapper subscriptionstyle={subscriptionStyle} data-sentry-element="AvatarWrapper" data-sentry-source-file="index.tsx">
                            {user.avatarUrl ? <AvatarImage src={user.avatarUrl || "https://charsheet-gen.s3.eu-west-2.amazonaws.com/charsheet-gen/px-conversions/PainterlyFantasyPortrait.webp"} alt={user.username} /> : <DefaultAvatar>
                                    <User size={32} color={colourScheme.fullWhite} />
                                </DefaultAvatar>}
                        </AvatarWrapper>
                        <div className="flex-grow">
                            <UserInfo data-sentry-element="UserInfo" data-sentry-source-file="index.tsx">
                                <Username data-sentry-element="Username" data-sentry-source-file="index.tsx">{user.username}</Username>
                            </UserInfo>
                            <div className="flex items-center mt-2 gap-2">
                                {user.subscriptionType && <div className="text-xs font-medium px-2 py-0.5 rounded-full inline-block" style={{
                backgroundImage: user.subscriptionType === UserPremiumType.ELITE ? `linear-gradient(45deg, ${colourScheme.success}, ${colourScheme.alert}, ${colourScheme.secondary})` : user.subscriptionType === UserPremiumType.ULTIMATE ? `linear-gradient(45deg, ${colourScheme.secondaryDark}, ${colourScheme.errorDark}, ${colourScheme.alert})` : "none",
                backgroundColor: user.subscriptionType === UserPremiumType.BASIC ? colourScheme.secondaryDark : user.subscriptionType === UserPremiumType.PLUS ? colourScheme.errorDark : user.subscriptionType === UserPremiumType.PRO ? colourScheme.alert : user.subscriptionType === UserPremiumType.NONE ? colourScheme.greyLight : "transparent",
                color: colourScheme.white
              }}>
                                        {user.subscriptionType.toLowerCase().charAt(0).toUpperCase() + user.subscriptionType.toLowerCase().slice(1)}
                                    </div>}

                                <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                                    <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                        <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                            <div className="flex items-center rounded-full" style={{
                      color: colourScheme.errorDark
                    }}>
                                                <Flame className="h-4 w-4 flex-shrink-0" data-sentry-element="Flame" data-sentry-source-file="index.tsx" />
                                                <span className="text-sm mt-0.5 leading-none">
                                                    {user.streak}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                            <p>Current Login Streak</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    </Header>
                    <StatsContainer data-sentry-element="StatsContainer" data-sentry-source-file="index.tsx">
                        <StatItem data-sentry-element="StatItem" data-sentry-source-file="index.tsx">
                            <StatText data-sentry-element="StatText" data-sentry-source-file="index.tsx">
                                <StatIcon data-sentry-element="StatIcon" data-sentry-source-file="index.tsx">
                                    <Users size={16} data-sentry-element="Users" data-sentry-source-file="index.tsx" />
                                </StatIcon>
                                <StatValue data-sentry-element="StatValue" data-sentry-source-file="index.tsx">
                                    {user.stats?.["Followers"] || 0}
                                </StatValue>
                            </StatText>
                            <StatLabel data-sentry-element="StatLabel" data-sentry-source-file="index.tsx">Followers</StatLabel>
                        </StatItem>
                        <StatItem data-sentry-element="StatItem" data-sentry-source-file="index.tsx">
                            <StatText data-sentry-element="StatText" data-sentry-source-file="index.tsx">
                                <StatIcon data-sentry-element="StatIcon" data-sentry-source-file="index.tsx">
                                    <Heart size={16} data-sentry-element="Heart" data-sentry-source-file="index.tsx" />
                                </StatIcon>
                                <StatValue data-sentry-element="StatValue" data-sentry-source-file="index.tsx">
                                    {user.stats?.["Total Likes"] || 0}
                                </StatValue>
                            </StatText>
                            <StatLabel data-sentry-element="StatLabel" data-sentry-source-file="index.tsx">Likes</StatLabel>
                        </StatItem>
                        <StatItem data-sentry-element="StatItem" data-sentry-source-file="index.tsx">
                            <StatText data-sentry-element="StatText" data-sentry-source-file="index.tsx">
                                <StatIcon data-sentry-element="StatIcon" data-sentry-source-file="index.tsx">
                                    <ImageIcon size={16} data-sentry-element="ImageIcon" data-sentry-source-file="index.tsx" />
                                </StatIcon>
                                <StatValue data-sentry-element="StatValue" data-sentry-source-file="index.tsx">
                                    {user.stats?.["Images Generated"] || 0}
                                </StatValue>
                            </StatText>
                            <StatLabel data-sentry-element="StatLabel" data-sentry-source-file="index.tsx">Images</StatLabel>
                        </StatItem>
                    </StatsContainer>
                    {user.username !== state.username && <FollowButton onClick={handleFollowToggle} isFollowing={isFollowing} aria-label={isFollowing ? "Unfollow user" : "Follow user"}>
                            {isFollowing ? <>
                                    <UserMinus size={18} />
                                    Unfollow
                                </> : <>
                                    <UserPlus size={18} />
                                    Follow
                                </>}
                        </FollowButton>}
                </Content>
            </CardContainer>
        </Link>;
};
export default UserCard;